'use client'

import React from 'react'
import type { PropsWithChildren } from 'react'

import BottomSheet from '@/components/ui/bottomsheet'
import Modal from '@/components/ui/modal'

const MOBILE_BREAKPOINT = 1024

type DialogVariant = 'modal' | 'bottomsheet'

type DialogProps = PropsWithChildren<{}> & {
  isOpen: boolean
  onClose: () => void
  size?: 'sm' | 'md' | 'lg'
  outsideClickClose?: boolean
}

const DialogContext = React.createContext<{ variant: DialogVariant } | null>(
  null
)

const getInitialVariant = (): DialogVariant => {
  if (typeof window === 'undefined') {
    return 'modal'
  }

  return window.innerWidth < MOBILE_BREAKPOINT ? 'bottomsheet' : 'modal'
}

const Dialog = ({
  children,
  isOpen,
  onClose,
  size = 'sm',
  outsideClickClose = true
}: DialogProps) => {
  const [variant, setVariant] = React.useState<DialogVariant>(getInitialVariant)

  React.useEffect(() => {
    const handleResize = () => {
      const nextVariant =
        window.innerWidth < MOBILE_BREAKPOINT ? 'bottomsheet' : 'modal'
      setVariant(nextVariant)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const content = (
    <DialogContext.Provider value={{ variant }}>
      {children}
    </DialogContext.Provider>
  )

  if (variant === 'bottomsheet') {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        isCloseClickOutside={outsideClickClose}
      >
        {content}
      </BottomSheet>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      outsideClickClose={outsideClickClose}
    >
      {content}
    </Modal>
  )
}

type DialogHeaderProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLDivElement>

const DialogHeader = ({ children, className, ...props }: DialogHeaderProps) => {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('Dialog.Header must be used within a Dialog component')
  }

  if (context.variant === 'bottomsheet') {
    return (
      <BottomSheet.Header
        {...props}
        className={`${className} flex-col-reverse`}
      >
        {children}
      </BottomSheet.Header>
    )
  }

  return <Modal.Header {...props}>{children}</Modal.Header>
}

type DialogTitleProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLParagraphElement>

const DialogTitle = ({ children, ...props }: DialogTitleProps) => {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('Dialog.Title must be used within a Dialog component')
  }

  if (context.variant === 'bottomsheet') {
    return <BottomSheet.Title {...props}>{children}</BottomSheet.Title>
  }

  return <Modal.Title {...props}>{children}</Modal.Title>
}

const DialogClose = () => {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('Dialog.Close must be used within a Dialog component')
  }

  if (context.variant === 'bottomsheet') {
    return <BottomSheet.Close />
  }

  return <Modal.Close />
}

type DialogContentProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLDivElement>

const DialogContent = ({ children, ...props }: DialogContentProps) => {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('Dialog.Content must be used within a Dialog component')
  }

  if (context.variant === 'bottomsheet') {
    return <BottomSheet.Content {...props}>{children}</BottomSheet.Content>
  }

  return <Modal.Content {...props}>{children}</Modal.Content>
}

type DialogActionsProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLDivElement>

const DialogActions = ({ children, ...props }: DialogActionsProps) => {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('Dialog.Actions must be used within a Dialog component')
  }

  if (context.variant === 'bottomsheet') {
    return <BottomSheet.Actions {...props}>{children}</BottomSheet.Actions>
  }

  return <Modal.Actions {...props}>{children}</Modal.Actions>
}

Dialog.Header = DialogHeader
Dialog.Title = DialogTitle
Dialog.Close = DialogClose
Dialog.Content = DialogContent
Dialog.Actions = DialogActions

export default Dialog
